"use client";

import React, { useState, useRef, useEffect } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollSmoother } from "gsap/all";
import { useTransition } from "@/context/TransitionContext";
import { usePathname } from "next/navigation";
import Image from "next/image";

type MenuItem =
  | { label: string; kind: "scroll"; id: string }
  | { label: string; kind: "route"; href: string };

const NavBar = () => {
  const { triggerTransition } = useTransition();
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const tl = useRef<gsap.core.Timeline | null>(null);

  const menuItems: MenuItem[] = [
    { label: "About Us", kind: "route", href: "/about-us" },
    { label: "Restaurants", kind: "route", href: "/restaurants" },
    { label: "Caterers", kind: "route", href: "/caterers" },
    { label: "Flavors", kind: "scroll", id: "flavors" },
    { label: "Benefits", kind: "scroll", id: "benefits" },
    { label: "Contact Us", kind: "route", href: "/contact-us" },
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavClick = (targetId: string) => {
    setIsMenuOpen(false);

    if (typeof window === "undefined") return;

    const target = document.getElementById(targetId);
    if (!target) return;

    const yOffset = -64; // approximate navbar height
    const rect = target.getBoundingClientRect();
    const smoother = ScrollSmoother.get();
    const currentY = smoother ? smoother.scrollTop() : window.scrollY;
    const scrollTargetY = currentY + rect.top + yOffset;

    if (smoother) {
      smoother.scrollTo(scrollTargetY, true);
      return;
    }

    window.scrollTo({
      top: scrollTargetY,
      behavior: "smooth",
    });
  };

  const handleRouteNavigation = (href: string) => {
    setIsMenuOpen(false);
    if (typeof window === "undefined") return;

    if (pathname === href) {
      window.location.assign(href);
      return;
    }

    triggerTransition(href);
  };

  useGSAP(() => {
    tl.current = gsap.timeline({ paused: true })
      .to(menuRef.current, {
        y: "0%",
        duration: 0.8,
        ease: "power3.inOut",
        autoAlpha: 1,
      })
      .from(
        ".menu-link",
        {
          yPercent: 100,
          duration: 0.6,
          stagger: 0.1,
          ease: "power3.out",
        },
        "-=0.4"
      );
  }, { scope: menuRef });

  useEffect(() => {
    if (tl.current) {
      if (isMenuOpen) {
        tl.current.play();
      } else {
        tl.current.reverse();
      }
    }
  }, [isMenuOpen]);

  return (
    <>
      {/* --- Main Navbar (Fixed) --- */}
      <nav
        className={`fixed top-0 left-0 w-full z-[1000] flex items-center justify-between px-6 py-4 transition-colors duration-500 ${isMenuOpen ? "text-milk" : "text-dark-brown"}`}
      >
        {/* Left: Hamburger */}
        <div className="flex-1 flex justify-start">
          <button
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isMenuOpen}
            aria-controls="site-menu"
            className="relative w-8 h-8 flex flex-col justify-center items-center group cursor-pointer z-[1000]"
          >
            {/* Top Line */}
            <span
              className={`absolute block w-8 h-0.5 bg-current transition-all duration-300 ease-in-out ${isMenuOpen ? "rotate-45" : "-translate-y-1.5"
                }`}
            />

            {/* Middle Line */}
            <span
              className={`absolute block w-8 h-0.5 bg-current transition-all duration-300 ease-in-out ${isMenuOpen ? "opacity-0" : "opacity-100"
                }`}
            />

            {/* Bottom Line */}
            <span
              className={`absolute block w-8 h-0.5 bg-current transition-all duration-300 ease-in-out ${isMenuOpen ? "-rotate-45" : "translate-y-1.5"
                }`}
            />
          </button>
        </div>

        {/* Center: Logo */}
        <div className="flex-1 flex justify-center">
          <button onClick={() => triggerTransition("/")} aria-label="Go to home" className="relative z-[1000]">
            <div className="relative size-16 md:size-18">
              <div aria-hidden="true" className="absolute inset-[-4%] rounded-full bg-[#E24138]/96" />
              <div aria-hidden="true" className="absolute inset-[-11%] rounded-full bg-[#EA6F69]/76" />
              <div aria-hidden="true" className="absolute inset-[-19%] rounded-full bg-[#F3A7A2]/40" />
              <div className="relative size-full rounded-full bg-[#D0150B] p-1.5">
                <Image
                  src="https://res.cloudinary.com/ddahd2kco/image/upload/v1770472227/logo_z0oeaw.jpg"
                  alt="Kunafa World Logo"
                  fill
                  className="object-cover scale-[0.8] rounded-full"
                  sizes="(max-width: 768px) 72px, 88px"
                  priority
                />
              </div>
            </div>
          </button>
        </div>

        <div className="flex-1 flex justify-end" />
      </nav>

      {/* --- Full Screen Menu Overlay --- */}
      <div
        id="site-menu"
        ref={menuRef}
        className="fixed inset-0 z-[990] bg-dark-brown text-milk flex invisible -translate-y-full"
      >
        <div className="w-full h-full flex md:flex-row flex-col">

          {/* Left Column: Navigation Links */}
          <div className="md:w-1/2 w-full h-full flex flex-col justify-center items-center gap-4 relative pt-24 pb-10">
            <div className="flex flex-col gap-2 text-center group/menu leading-none">
              {menuItems.map((item) => (
                <div key={item.kind === "scroll" ? item.id : item.href} className="py-1">
                  <button
                    type="button"
                    className="menu-link w-full text-[12vw] md:text-[5vw] font-bold uppercase text-white opacity-100 transition-all duration-300 hover:!opacity-100 hover:text-light-brown"
                    onClick={() =>
                      item.kind === "scroll"
                        ? handleNavClick(item.id)
                        : handleRouteNavigation(item.href)
                    }
                  >
                    {item.label}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Visual / Image (Optional for now, placeholder) */}
          <div className="hidden md:flex w-1/2 h-full bg-[#3d2318] relative overflow-hidden">
            <Image
              src="https://res.cloudinary.com/ddahd2kco/image/upload/v1770472257/nav-bg_sg1lb0.png"
              alt="Menu Background"
              fill
              className="object-cover"
              sizes="50vw"
            />
          </div>

        </div>
      </div>
    </>
  );
};

export default NavBar;
